export default {
  // Page Header
  page_header: {
    title: "Channels",
    description: "Monitor instrument connections and manage communication channels for Iris Core.",
    metadata: {
      locations: {
        label: "Locations",
        tooltip: "Locations with configured communication channels",
      },
      instruments: {
        label: "Instruments",
        tooltip: "Instruments connected to at least one channel",
      },
      running: {
        label: "Running",
        tooltip: "Channels currently started by Iris Core",
      },
      connected: {
        label: "Connected",
        tooltip: "Channels with an active TCP connection to an instrument",
      },
      disabled: {
        label: "Disabled",
        tooltip: "Channels disabled in configuration",
      },
    },
    action_buttons: {
      add: {
        label: "Add Channel",
        tooltip: "Add a communication channel",
      },
      refresh: {
        label: "Refresh",
        tooltip: "Refresh the list of channels",
      },
    },
  },

  // Content
  channel_card: {
    action_buttons: {
      start: "Start",
      stop: "Stop",
      console: "Remote Desktop",
      console_description: "Open a remote desktop session for this channel",
      edit: "Edit channel",
      edit_description: "Change protocol, port, instrument or enabled state",
      delete: "Delete channel",
      delete_description: "Remove this communication channel",
      more_actions: "More actions",
      tests: "Tests",
      tests_description: "View and manage instrument tests",
    },
  },
  channels_table: {
    tabs: {
      all: "Channels",
    },
    summary: {
      running: "{count} running",
      connected: "{count} connected",
      disabled: "{count} disabled",
    },
    columns: {
      channel: "Channel",
      instrument: "Instrument",
      driver: "Driver",
      driver_tooltip:
        "Communication protocol driver used to parse messages from this instrument (e.g. HL7 MLLP, ASTM, LIS02). Must match the instrument's actual protocol.",
      port: "Port",
      port_tooltip: "TCP port this channel listens on for incoming data from the instrument.",
      enabled: "Enabled",
      enabled_column_tooltip: "Enabled channels can be started; disabled channels are excluded from processing.",
      note: "Note",
    },
    collapse_group: "Collapse location group",
    expand_group: "Expand location group",
    navigate_instrument: "Open instrument detail",
    filter_location: "Location",
    filter_location_all: "All locations",
    filter_location_tooltip: "Filter channels by location",
    filter_location_active_tooltip: "Showing channels at: {name}. Click to change.",
    empty: {
      title: "No channels available",
      description: "Create a communication channel to connect an instrument to Iris Core.",
    },
  },
  channel_crud: {
    add: "Add channel",
    add_title: "Add channel",
    add_subtitle: "Create the communication link to an instrument",
    onboarding_title: "Configure communication channel",
    onboarding_subtitle: "Final step after creating the instrument",
    onboarding_hint:
      "The instrument is created. Now select its protocol / driver and TCP port so Iris can communicate with it.",
    edit_title: "Edit channel",
    view_title: "Channel details",
    delete_title: "Delete channel",
    delete_message: "This channel will be permanently removed.",
    delete_running_connected_message:
      "This channel is running and a device is actively connected. Stopping it will close the live TCP session and permanently remove the channel.",
    delete_running_message:
      "This channel is running and listening for connections. It will be stopped and permanently removed.",
    delete_stopped_connected_message:
      "This channel is stopped, but a device still has an open connection. Deleting will close it permanently.",
    protocol_hint:
      "The protocol is selected through the instrument driver. A channel connects one instrument to Iris using a driver and TCP port.",
    view_mode_edit_btn: "Edit",
    view_mode_btn: "Preview",
    view_subtitle: "Read-only preview — click Edit to make changes",
    edit_mode_confirm_btn: "Edit Mode",
    running_edit_blocked: "Channel is running — stop it before editing",
    running_banner: "This channel is currently running. Stop it before making changes.",
    stop_and_edit: "Stop Channel & Edit",
    stop_and_delete: "Stop Channel & Delete",
    stop_and_delete_confirm_title: "Stop and delete channel",
    stop_and_delete_confirm_body:
      "This will stop the channel, close any active TCP sessions, and permanently delete it. This action cannot be undone.",
    delete_confirm_title: "Permanently delete channel",
    delete_confirm_body: "This channel will be permanently removed and cannot be recovered. Are you sure?",
    running_blocked_title: "Channel is running",
    running_blocked_body:
      "This channel is currently running and accepting TCP connections. Editing is not allowed while the channel is active — stop it first to make configuration changes.",
    edit_confirm_title: "Edit channel",
    edit_confirm_body:
      "You are about to switch to edit mode. You can change the channel configuration: name, port, instrument, protocol / driver, enabled state, and note.",
    save_confirm_title: "Save changes",
    save_confirm_body:
      "Changes will be sent to Iris Core and shown in the list only after the server confirms the update.",
    unsaved_title: "Unsaved changes",
    unsaved_save_and_close: "Save and continue",
    unsaved_discard: "Discard",
    unsaved_stay: "Stay",
    fields: {
      name: "Channel name",
      name_hint: "Visible name for this communication link",
      name_placeholder: "e.g. Cobas e801 TCP channel",
      port: "Port",
      port_hint: "TCP port used by Iris Core to communicate with the instrument",
      port_placeholder: "e.g. 5001",
      instrument: "Instrument",
      instrument_hint: "Physical instrument that this channel connects to",
      instrument_lis_id_label: "LIS ID",
      driver: "Protocol / driver",
      driver_hint: "Protocol and model for the specific instrument",
      enabled: "Enabled",
      enabled_active: "Enabled",
      enabled_inactive: "Disabled",
      enabled_active_hint: "Channel is active and accepting TCP connections",
      enabled_inactive_hint: "Channel is deactivated",
      note: "Note",
      note_hint: "Optional operational note for this channel",
      note_placeholder: "e.g. Primary analyzer interface, bench 2",
      note_empty: "No note",
    },
    form_sections: {
      basic: "Basic settings",
      connection: "Instrument connection",
      operation: "State and note",
    },
    validation: {
      name_required: "Channel name is required",
      name_taken: "A channel with this name already exists",
      name_too_long: "Channel name is too long — maximum {max} characters",
      port_required: "Port is required",
      port_taken: "This port is already in use by another channel",
      instrument_required: "Instrument is required",
      driver_required: "Protocol / driver is required",
      note_too_long: "Note is too long — maximum {max} characters",
    },
    driver_picker_search: "Search by manufacturer or model…",
    driver_no_results: "No drivers found for your search.",
    snackbar_created: "Channel {name} was created",
    snackbar_updated: "Channel {name} was updated",
    snackbar_deleted: "Channel {name} was deleted",
    snackbar_save_failed: "Channel could not be saved — please check your input and try again",
    snackbar_delete_failed: "Channel could not be deleted — please try again",
    snackbar_stop_before_delete_failed: "Channel {name} could not be stopped — it was not deleted",
    subtitle_channel: "Channel",
    subtitle_channel_id: "Channel ID",
    subtitle_channel_id_tooltip: "Unique identifier of this channel in the system",
    subtitle_location: "Location",
    subtitle_location_tooltip: "Location the instrument belongs to — e.g. lab room, ward, or building",
  },
  channel: "channel",
  channels: "channels",
  online: "Online",
  offline: "Offline",
  connected: "Connected",
  not_connected: "Not Connected",
  channel_status: {
    disabled: "Channel is disabled",
    stopped: "Stopped, ready to start",
    stopped_connected: "Stopped, device still connected",
    running_waiting: "Running, no TCP connection",
    running_connected: "Running and connected",
  },
  instrument_name: "Instrument Name",
  driver_id: "Driver ID",
  port: "Port",

  // Empty State
  no_channels_title: "No Channels Available",
  no_channels_description: "There are currently no channels configured. Please check your connection or refresh.",
  refresh_channels: "Refresh",
};
